/* eslint-disable */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@material-ui/core';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

const monthNames = {
  1: 'Jan',
  2: 'Feb',
  3: 'Mar',
  4: 'Apr',
  5: 'May',
  6: 'Jun',
  7: 'Jul',
  8: 'Aug',
  9: 'Sep',
  10: 'Oct',
  11: 'Nov',
  12: 'Dec',
};

const getXAxisCategories = (data) => Object.keys(data).map((month) => monthNames[month]);

const getTargetSeries = (length, target) => ({
  name: 'Top Performer',
  data: new Array(length).fill(Number(target)),
  type: 'line',
  color: '#FF3E32',
  dashStyle: '',
  marker: {
    enabled: false,
    symbol: 'circle',
    radius: 6,
    fillColor: '#FF3E32',
  },
  lineWidth: 6,
  showInLegend: true,
  legendIndex: 1,
  states: {
    hover: {
      lineWidthPlus: 0,
    },
  },
});

const HistoricPerformanceGross = ({
  rollingDataGross,
  yearlyGrossData,
  monthlyGrossData,
  userSelectionRevenue1,
  currentYearDataGross,
  yearlyRevenueGross,
}) => {
  const defaultXAxisCategories = useMemo(() => getXAxisCategories(rollingDataGross), [
    rollingDataGross,
  ]);
  const montlyXAxisCategories = useMemo(() => getXAxisCategories(monthlyGrossData), [
    monthlyGrossData,
  ]);
  const currentYearXAxisCategories = useMemo(() => getXAxisCategories(currentYearDataGross), [
    currentYearDataGross,
  ]);

  const grossProfitTarget = useMemo(() => {
    return (
      yearlyRevenueGross.find(
        (target) =>
          target.questionDetails.sectionDetails.typeShortName ===
            'VectorTargets' + new Date().getFullYear() &&
          target.questionDetails.text.includes('Gross Profit'),
      )?.answer || 0
    );
  }, [yearlyRevenueGross]);

  const defaultSeries = useMemo(
    () => [
      {
        name: 'My Performance',
        data: Object.values(rollingDataGross).map((item) => item.operationalProfit),
        color: '#003C6F',
      },
    ],
    [rollingDataGross],
  );

  const baseChartOptions = useMemo(
    () => ({
      chart: {
        type: 'column',
      },
      title: {
        text: '',
      },
      xAxis: {
        categories: defaultXAxisCategories,
      },
      yAxis: {
        max: Math.max(...defaultSeries[0].data) * 5.2,
        title: {
          text: 'USD',
        },
      },
      legend: {
        useHTML: true,
        symbolHeight: 12,
        symbolWidth: 12,
        symbolRadius: 6,
      },
      plotOptions: {
        column: {
          color: '#003C6F',
        },
      },
    }),
    [defaultXAxisCategories, defaultSeries],
  );

  const defaultChartData = useMemo(
    () => ({
      ...baseChartOptions,
      series: defaultSeries.concat(
        getTargetSeries(defaultXAxisCategories.length, grossProfitTarget),
      ),
    }),
    [baseChartOptions, defaultSeries, defaultXAxisCategories.length, grossProfitTarget],
  );

  const monthlyChartData = useMemo(
    () => ({
      ...baseChartOptions,
      xAxis: {
        categories: montlyXAxisCategories,
      },
      series: [
        {
          name: 'My Performance',
          data: Object.values(monthlyGrossData).map((item) => item.operationalProfit),
          color: '#003C6F',
        },
      ].concat(getTargetSeries(montlyXAxisCategories.length, grossProfitTarget)),
    }),
    [baseChartOptions, montlyXAxisCategories, monthlyGrossData, grossProfitTarget],
  );

  const sameYearChartData = useMemo(
    () => ({
      ...baseChartOptions,
      xAxis: {
        categories: currentYearXAxisCategories,
      },
      series: [
        {
          name: 'My Performance',
          data: Object.values(currentYearDataGross).map((item) => item.operationalProfit),
        },
      ].concat(getTargetSeries(currentYearXAxisCategories.length, grossProfitTarget)),
    }),
    [baseChartOptions, currentYearXAxisCategories, currentYearDataGross, grossProfitTarget],
  );

  const yearlyChartData = useMemo(() => {
    const yearlyDataSeries = [];
    const selectedYears = Object.keys(yearlyGrossData);

    selectedYears.forEach((year, index) => {
      const yearData = yearlyGrossData[year];
      const countryName = 'Gross Profit';
      yearData.forEach((countryData) => {
        const operationalProfit = countryData.operationalProfit;
        let countrySeriesItem = yearlyDataSeries.find(
          (seriesItem) => seriesItem.name === countryName,
        );
        if (!countrySeriesItem) {
          countrySeriesItem = {
            name: countryName,
            data: new Array(selectedYears.length).fill(null),
            color: '#003C6F',
          };
          yearlyDataSeries.push(countrySeriesItem);
        }
        countrySeriesItem.data[index] = operationalProfit;
      });
    });

    const targetYearIndex = selectedYears.indexOf("'" + new Date().getFullYear() + "'");
    if (targetYearIndex !== -1) {
      const targetSeriesData = new Array(selectedYears.length).fill(null);
      targetSeriesData[targetYearIndex] = Number(grossProfitTarget);
      yearlyDataSeries.push({
        name: 'Top Performer',
        data: targetSeriesData,
        type: 'line',
        color: '#FF3E32',
        dashStyle: '',
        marker: {
          enabled: false,
          symbol: 'line',
          radius: 6,
          fillColor: '#FF3E32',
        },
        lineWidth: 6,
        legendIndex: 1,
        states: {
          hover: {
            lineWidthPlus: 0,
          },
        },
        showInLegend: true,
      });
    }

    return {
      ...baseChartOptions,
      xAxis: {
        categories: selectedYears,
      },
      series: yearlyDataSeries,
    };
  }, [baseChartOptions, yearlyGrossData, grossProfitTarget]);

  let chartOptions;
  switch (userSelectionRevenue1) {
    case 'monthly':
      chartOptions = monthlyChartData;
      break;
    case 'yearly':
      chartOptions = yearlyChartData;
      break;
    case 'currentYear':
      chartOptions = sameYearChartData;
      break;
    default:
      chartOptions = defaultChartData;
      break;
  }

  return (
    <Box mt={1}>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </Box>
  );
};

HistoricPerformanceGross.propTypes = {
  rollingDataGross: PropTypes.object.isRequired,
  yearlyGrossData: PropTypes.object.isRequired,
  monthlyGrossData: PropTypes.object.isRequired,
  userSelectionRevenue1: PropTypes.string.isRequired,
  currentYearDataGross: PropTypes.object.isRequired,
  yearlyRevenueGross: PropTypes.array.isRequired,
};

export default HistoricPerformanceGross;
